/**
 * PDF取込（下敷き）用: アップロードされたファイルを背景画像（base64）に変換する。
 * - PNG/JPEG はそのまま
 * - PDF は pdfjs-dist で1ページ目をラスタライズ（scale 2 ≒ 150dpi 相当）
 * pdfjs-dist はサイズが大きいため動的 import で必要時のみ読み込む。
 */
export type BackgroundImage = {
  base64: string;
  contentType: "image/png" | "image/jpeg";
};

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export async function fileToBackgroundImage(file: File): Promise<BackgroundImage> {
  if (file.type === "image/png" || file.type === "image/jpeg") {
    return { base64: arrayBufferToBase64(await file.arrayBuffer()), contentType: file.type };
  }

  if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
    const pdfjs = await import("pdfjs-dist");
    const workerUrl = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

    const loadingTask = pdfjs.getDocument({ data: await file.arrayBuffer() });
    try {
      const doc = await loadingTask.promise;
      const page = await doc.getPage(1);
      const viewport = page.getViewport({ scale: 2 });
      const canvas = document.createElement("canvas");
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      await page.render({ canvas, viewport }).promise;
      const dataUrl = canvas.toDataURL("image/png");
      return { base64: dataUrl.slice(dataUrl.indexOf(",") + 1), contentType: "image/png" };
    } finally {
      await loadingTask.destroy();
    }
  }

  throw new Error("PNG・JPEG・PDF のみ取り込めます");
}
